import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import {
  createColumnHelper,
  flexRender,
  useTable,
  type SortingState,
  type Table as TanstackTable,
} from "@tanstack/react-table";
import { appTableFeatures } from "@/lib/table-features";
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, Plus, Search } from "lucide-react";

import type { BillingOverview } from "@/components/billing/organization-billing-card";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { RowAction, RowActionSeparator, RowActionsMenu } from "@/components/row-actions-menu";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { InviteMemberDialog } from "./invite-member-dialog";
import { ROLE_LABELS, isPaidSeatRole, pendingCancellationInvitationNote, roleLabel, type OrgRole } from "./member-change-copy";
import { MemberChangeDialog, type MemberChange, type PendingChange } from "./member-change-dialog";
import {
  buildMemberRows,
  roleRank,
  rowDisplayName,
  statusRank,
  type MemberRow,
  type MemberStatus,
  type OrganizationInvitation,
  type OrganizationMember,
} from "./member-rows";
import { useOrganizationInvitations } from "./use-organization-invitations";

const ASSIGNABLE_ROLES: OrgRole[] = ["owner", "admin", "member", "reviewer"];

type StatusFilter = "current" | MemberStatus;
type RoleFilter = "all" | OrgRole;

const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  current: "All but revoked",
  active: "Active",
  pending: "Pending",
  expired: "Expired",
  revoked: "Revoked",
};

const STATUS_LABELS: Record<MemberStatus, string> = {
  active: "Active",
  pending: "Pending",
  expired: "Expired",
  revoked: "Revoked",
};

function MemberStatusBadge({ status }: { status: MemberStatus }) {
  if (status === "active") return <StatusBadge status="completed" label={STATUS_LABELS.active} size="sm" />;
  if (status === "pending") {
    return <StatusBadge status="pending" label={STATUS_LABELS.pending} size="sm" showSpinner={false} />;
  }
  return <StatusBadge status="pending" tone="muted" label={STATUS_LABELS[status]} size="sm" />;
}

function formatDate(time: number | null) {
  return time === null ? "—" : new Date(time).toLocaleDateString(undefined, { dateStyle: "medium" });
}

function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

type ReviewerSeatUsage = { allowance: number; activeReviewers: number; pendingInvitations: number };

/** A self-serve subscription set to end: invitations accepted before then add no charge. */
function pendingCancellation(overview: BillingOverview | undefined) {
  const billing = overview?.billing;
  const status = overview?.summary.billingStatus;
  const live = status === "active" || status === "trialing" || status === "past_due" || status === "unpaid";
  if (!live || !billing?.cancelAtPeriodEnd || billing.collectionMethod === "send_invoice") return null;
  return { endsAt: billing.currentPeriodEnd ?? null };
}

/** "3 paid seats · 2 of 5 free reviewer seats used". Over the allowance reads "6 of 5". */
function SeatSummary(props: { paidSeats: number | undefined; reviewerUsage: ReviewerSeatUsage | undefined }) {
  const parts: string[] = [];
  if (props.paidSeats !== undefined) parts.push(plural(props.paidSeats, "paid seat"));
  if (props.reviewerUsage) {
    const used = props.reviewerUsage.activeReviewers + props.reviewerUsage.pendingInvitations;
    parts.push(`${used} of ${props.reviewerUsage.allowance} free reviewer seats used`);
  }
  if (parts.length === 0) return null;
  return <p className="text-sm font-medium text-muted-foreground">{parts.join(" · ")}</p>;
}

function MemberActionsMenu(props: {
  member: OrganizationMember;
  viewerRole: string | undefined;
  isSelf: boolean;
  isLastOwner: boolean;
  onSelect: (change: MemberChange) => void;
}) {
  const userId = props.member._id as Id<"users">;
  // The last owner can't change role; ownership moves by transfer instead.
  const roles = props.isLastOwner ? [] : ASSIGNABLE_ROLES.filter((role) => role !== props.member.orgRole);
  const canTransfer = props.viewerRole === "owner" && !props.isSelf && props.member.orgRole !== "owner";
  const canRemove = !props.isSelf;
  if (roles.length === 0 && !canTransfer && !canRemove) return null;

  return (
    <RowActionsMenu label={`Manage ${props.member.email}`}>
      {roles.length > 0 ? (
        <>
          <DropdownMenuLabel>Change role</DropdownMenuLabel>
          {roles.map((role) => (
            <RowAction key={role} onSelect={() => props.onSelect({ kind: "change_role", userId, role })}>
              {ROLE_LABELS[role]}
            </RowAction>
          ))}
        </>
      ) : null}
      {canTransfer ? (
        <>
          {roles.length > 0 ? <RowActionSeparator /> : null}
          <RowAction onSelect={() => props.onSelect({ kind: "transfer_ownership", userId })}>
            Transfer ownership
          </RowAction>
        </>
      ) : null}
      {canRemove ? (
        <>
          <RowActionSeparator />
          <RowAction destructive onSelect={() => props.onSelect({ kind: "remove", userId })}>
            Remove from organization
          </RowAction>
        </>
      ) : null}
    </RowActionsMenu>
  );
}

function InvitationActionsMenu(props: {
  invitation: OrganizationInvitation;
  isWorking: boolean;
  onResend: () => void;
  onRevoke: () => void;
}) {
  const { invitation } = props;
  if (invitation.state !== "pending" && invitation.state !== "expired") return null;
  if (props.isWorking) {
    return (
      <span className="inline-flex size-8 items-center justify-center" role="status">
        <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Updating invitation for {invitation.email}</span>
      </span>
    );
  }
  return (
    <RowActionsMenu label={`Manage invitation for ${invitation.email}`}>
      <RowAction onSelect={props.onResend}>Resend invitation</RowAction>
      {invitation.state === "pending" ? (
        <>
          <RowActionSeparator />
          <RowAction destructive onSelect={props.onRevoke}>
            Revoke invitation
          </RowAction>
        </>
      ) : null}
    </RowActionsMenu>
  );
}

function SortableHeader(props: { label: string; sorted: false | "asc" | "desc"; onToggle: ((event: unknown) => void) | undefined }) {
  const Icon = props.sorted === "asc" ? ArrowUp : props.sorted === "desc" ? ArrowDown : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={props.onToggle}
      className="-mx-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 uppercase tracking-wider hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      {props.label}
      <Icon className={cn("size-3.5", props.sorted ? "text-foreground" : "text-muted-foreground")} aria-hidden="true" />
    </button>
  );
}

const columnHelper = createColumnHelper<typeof appTableFeatures, MemberRow>();

type ColumnMeta = { className?: string };

/** Per-render state the cells read, so the column definitions (and the menus in them) stay mounted. */
type TableContext = {
  viewerUserId: string | null | undefined;
  viewerRole: string | undefined;
  ownerCount: number;
  workingInvitationIds: ReadonlySet<string>;
  onMemberChange: (pending: PendingChange) => void;
  onResend: (invitation: OrganizationInvitation) => void;
  onRevoke: (invitation: OrganizationInvitation) => void;
};

function tableContext(table: TanstackTable<typeof appTableFeatures, MemberRow>) {
  return table.options.meta as TableContext;
}

const columns = columnHelper.columns([
  columnHelper.accessor((row) => rowDisplayName(row).toLowerCase(), {
    id: "name",
    header: "Name",
    cell: ({ row: { original: row }, table }) => (
      <div className="min-w-0 max-w-[9rem] sm:max-w-xs">
        <p className="truncate font-semibold">
          {rowDisplayName(row)}
          {row.kind === "member" && row.id === tableContext(table).viewerUserId ? (
            <span className="font-normal text-muted-foreground"> (you)</span>
          ) : null}
        </p>
        {row.name ? <p className="truncate text-xs text-muted-foreground">{row.email}</p> : null}
        {/* The role column is hidden on phones; keep the role visible under the name. */}
        <p className="text-xs font-medium sm:hidden">{roleLabel(row.role)}</p>
      </div>
    ),
  }),
  columnHelper.accessor((row) => roleRank(row.role), {
    id: "role",
    header: "Role",
    meta: { className: "hidden sm:table-cell" } satisfies ColumnMeta,
    cell: ({ row }) => <span className="font-medium">{roleLabel(row.original.role)}</span>,
  }),
  columnHelper.accessor((row) => statusRank(row.status), {
    id: "status",
    header: "Status",
    cell: ({ row }) => <MemberStatusBadge status={row.original.status} />,
  }),
  columnHelper.display({
    id: "seat",
    header: "Seat",
    meta: { className: "hidden md:table-cell" } satisfies ColumnMeta,
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {isPaidSeatRole(row.original.role) ? "Paid" : row.original.role === "reviewer" ? "Free" : "—"}
      </span>
    ),
  }),
  columnHelper.accessor((row) => row.date ?? 0, {
    id: "date",
    header: "Date",
    meta: { className: "hidden sm:table-cell" } satisfies ColumnMeta,
    cell: ({ row: { original: row } }) => (
      <span className="text-muted-foreground">
        {row.kind === "member" ? "Joined" : "Invited"} {formatDate(row.date)}
      </span>
    ),
  }),
  columnHelper.display({
    id: "actions",
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: "w-12 text-right" } satisfies ColumnMeta,
    cell: ({ row: { original: row }, table }) => {
      const context = tableContext(table);
      return row.kind === "member" ? (
        <MemberActionsMenu
          member={row.member}
          viewerRole={context.viewerRole}
          isSelf={row.id === context.viewerUserId}
          isLastOwner={row.role === "owner" && context.ownerCount <= 1}
          onSelect={(change) => context.onMemberChange({ change, member: row.member })}
        />
      ) : (
        <InvitationActionsMenu
          invitation={row.invitation}
          isWorking={context.workingInvitationIds.has(row.id)}
          onResend={() => context.onResend(row.invitation)}
          onRevoke={() => context.onRevoke(row.invitation)}
        />
      );
    },
  }),
]);

/**
 * Everyone in the organization in one table: active members and open invitations, with their
 * role, status, and seat. Owners and admins manage people from each row's menu.
 */
export function MembersPanel(props: { viewerUserId: string | null | undefined; viewerRole: string | undefined }) {
  const membersQuery = useQuery(convexQuery(api.auth.listOrganizationMembers, {}));
  const { data: reviewerUsage } = useQuery(convexQuery(api.reviewers.getOrganizationReviewerUsage, {}));
  const { data: billingOverview } = useQuery(convexQuery(api.billing.queries.getOrganizationBillingOverview, {}));
  const invitations = useOrganizationInvitations();

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("current");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);
  const [revoking, setRevoking] = useState<OrganizationInvitation | null>(null);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const members = membersQuery.data as OrganizationMember[] | undefined;
  const cancellation = pendingCancellation(billingOverview as BillingOverview | undefined);
  const hasPendingPaidInvitations = (invitations.invitations ?? []).some(
    (invitation) => invitation.state === "pending" && invitation.role !== "reviewer"
  );
  const ownerCount = members?.filter((member) => member.orgRole === "owner").length ?? 0;

  const rows = useMemo(
    () => buildMemberRows(members ?? [], invitations.invitations ?? []),
    [members, invitations.invitations]
  );
  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (roleFilter !== "all" && row.role !== roleFilter) return false;
      if (statusFilter === "current" ? row.status === "revoked" : row.status !== statusFilter) return false;
      return !query || row.email.toLowerCase().includes(query) || (row.name?.toLowerCase().includes(query) ?? false);
    });
  }, [rows, search, roleFilter, statusFilter]);
  const isFiltered = search.trim() !== "" || roleFilter !== "all" || statusFilter !== "current";


  const table = useTable({
    features: appTableFeatures,
    data: filteredRows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    // Every column sorts ascending first, including the numeric role, status, and date ranks.
    sortDescFirst: false,
    getRowId: (row) => `${row.kind}:${row.id}`,
    meta: {
      viewerUserId: props.viewerUserId,
      viewerRole: props.viewerRole,
      ownerCount,
      workingInvitationIds: invitations.workingIds,
      onMemberChange: setPendingChange,
      onResend: (invitation) => void invitations.resend(invitation),
      onRevoke: setRevoking,
    } satisfies TableContext,
  });

  const clearFilters = () => {
    setSearch("");
    setRoleFilter("all");
    setStatusFilter("current");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SeatSummary paidSeats={billingOverview?.summary.billableSeats} reviewerUsage={reviewerUsage} />
        <Button size="sm" className="ml-auto" onClick={() => setIsInviteOpen(true)}>
          <Plus aria-hidden="true" />
          Add member
        </Button>
      </div>
      {cancellation && hasPendingPaidInvitations ? (
        <p className="text-sm text-muted-foreground">{pendingCancellationInvitationNote(cancellation.endsAt)}</p>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row" role="search" aria-label="Filter members">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name or email"
            aria-label="Search name or email"
            className="pl-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={(value) => setRoleFilter(value as RoleFilter)}>
          <SelectTrigger className="sm:w-40" aria-label="Filter by role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ASSIGNABLE_ROLES.map((role) => (
              <SelectItem key={role} value={role}>{ROLE_LABELS[role]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
          <SelectTrigger className="sm:w-44" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(STATUS_FILTER_LABELS) as StatusFilter[]).map((status) => (
              <SelectItem key={status} value={status}>{STATUS_FILTER_LABELS[status]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                const canSort = header.column.getCanSort();
                return (
                  <TableHead
                    key={header.id}
                    className={(header.column.columnDef.meta as ColumnMeta | undefined)?.className}
                    aria-sort={canSort ? (sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none") : undefined}
                  >
                    {canSort ? (
                      <SortableHeader
                        label={String(header.column.columnDef.header)}
                        sorted={sorted}
                        onToggle={header.column.getToggleSortingHandler()}
                      />
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {membersQuery.isPending ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columns.length} className="py-8 text-center text-muted-foreground">
                <span className="inline-flex items-center gap-2" role="status">
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Loading members…
                </span>
              </TableCell>
            </TableRow>
          ) : membersQuery.error ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columns.length} className="py-8 text-center text-muted-foreground" role="alert">
                Members couldn't load. Refresh the page to try again.
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columns.length} className="py-8 text-center text-muted-foreground">
                {isFiltered ? (
                  <span className="inline-flex flex-wrap items-center justify-center gap-2">
                    No one matches these filters.
                    <Button variant="link" className="h-auto p-0" onClick={clearFilters}>Clear filters</Button>
                  </span>
                ) : (
                  "No members yet."
                )}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className={(cell.column.columnDef.meta as ColumnMeta | undefined)?.className}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {invitations.error ? (
        <p className="text-sm text-muted-foreground" role="alert">{invitations.error}</p>
      ) : invitations.invitations === null && !membersQuery.isPending ? (
        <p className="inline-flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Loading invitations…
        </p>
      ) : null}

      {pendingChange ? <MemberChangeDialog pending={pendingChange} onClose={() => setPendingChange(null)} /> : null}

      <ConfirmationDialog
        open={revoking !== null}
        onOpenChange={(open) => !open && setRevoking(null)}
        title="Revoke invitation?"
        description={`${revoking?.email ?? ""} won't be able to join with this invitation. You can invite them again later.`}
        confirmLabel="Revoke invitation"
        confirmVariant="destructive"
        onConfirm={() => (revoking ? invitations.revoke(revoking) : undefined)}
      />

      <InviteMemberDialog
        open={isInviteOpen}
        onOpenChange={setIsInviteOpen}
        onInvited={() => void invitations.refresh()}
      />
    </div>
  );
}
